import { createSlice } from '@reduxjs/toolkit';
import type { AgentSettingsState } from '../../types/agentSettings';
import { agentQueryKey, markStaleByOwner } from '../../utils/negotiationAgents/queryKey';
import { mutateNegotiationAgent } from '../negotiationAgents/actions';
import { fetchAgentSettingsPage, mutateAgentSetting, requestAgentSettingDocument } from './actions';

const initialState: AgentSettingsState = { pages: {}, mutations: {}, documents: {} };
const slice = createSlice({
  name: 'agentSettings',
  initialState,
  reducers: {
    clearSettingMutation(state, action: { payload: string }) {
      if (state.mutations[action.payload]?.status !== 'loading')
        delete state.mutations[action.payload];
    },
    clearSettingDocument(state, action: { payload: string }) {
      delete state.documents[action.payload];
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchAgentSettingsPage.pending, (state, action) => {
      const key = agentQueryKey(action.meta.arg);
      state.pages[key] = {
        data: state.pages[key]?.data ?? null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
        stale: false,
      };
    });
    builder.addCase(fetchAgentSettingsPage.fulfilled, (state, action) => {
      const entry = state.pages[action.payload.key];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload.page;
      entry.status = 'success';
    });
    builder.addCase(fetchAgentSettingsPage.rejected, (state, action) => {
      const entry = state.pages[agentQueryKey(action.meta.arg)];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Não foi possível carregar as configurações.',
      };
    });
    builder.addCase(mutateAgentSetting.pending, (state, action) => {
      state.mutations[action.meta.arg.agentId] = {
        settingId: 'settingId' in action.meta.arg ? action.meta.arg.settingId : undefined,
        data: null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(mutateAgentSetting.fulfilled, (state, action) => {
      const entry = state.mutations[action.meta.arg.agentId];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload;
      entry.status = 'success';
      const input = action.meta.arg;
      if (input.operation === 'save' && input.settingId) {
        for (const page of Object.values(state.pages)) {
          for (const item of page.data?.items ?? []) {
            if ('categoryName' in item && item.id === input.settingId) {
              item.name = input.data.name;
              item.content = input.data.content;
              if (action.payload.revision !== undefined) item.revision = action.payload.revision;
              if (input.data.kind) item.kind = input.data.kind;
              if (input.data.kind === 'TEXT') {
                item.documentId = null;
                item.document = null;
              } else if (input.data.documentId !== undefined) {
                const documentId = input.data.documentId;
                if (item.documentId !== documentId) {
                  item.document =
                    Object.values(state.documents).find(
                      (request) => request.data?.id === documentId,
                    )?.data ?? null;
                }
                item.documentId = documentId;
              }
            }
          }
        }
      }
      markStaleByOwner(state.pages, JSON.stringify(['agentId', action.meta.arg.agentId]));
    });
    builder.addCase(mutateAgentSetting.rejected, (state, action) => {
      const entry = state.mutations[action.meta.arg.agentId];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Não foi possível salvar a configuração.',
      };
    });
    builder.addCase(mutateNegotiationAgent.fulfilled, (state, action) => {
      if (!action.meta.arg.agentId) return;
      markStaleByOwner(state.pages, JSON.stringify(['agentId', action.meta.arg.agentId]));
    });
    builder.addCase(requestAgentSettingDocument.pending, (state, action) => {
      const key = action.meta.arg.requestKey;
      state.documents[key] = {
        data: state.documents[key]?.data ?? null,
        status: 'loading',
        error: null,
        requestId: action.meta.requestId,
      };
    });
    builder.addCase(requestAgentSettingDocument.fulfilled, (state, action) => {
      const entry = state.documents[action.meta.arg.requestKey];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.data = action.payload;
      entry.status = 'success';
    });
    builder.addCase(requestAgentSettingDocument.rejected, (state, action) => {
      const entry = state.documents[action.meta.arg.requestKey];
      if (entry?.requestId !== action.meta.requestId) return;
      entry.status = 'error';
      entry.error = action.payload ?? {
        code: 'REQUEST_FAILED',
        message: 'Não foi possível processar o documento.',
      };
    });
  },
});
export const { clearSettingMutation, clearSettingDocument } = slice.actions;
export default slice.reducer;
